<template>
  <v-row>
    <v-col xs="12" md="12" lg="12">
      <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
      <div class="d-md-none vehicles-mobile-list mt-2 px-2 pb-4">
        <v-progress-linear
          v-if="loading"
          indeterminate
          color="red"
          class="mb-2"
        />

        <div
          v-for="(item, index) in cars"
          :key="item._id"
          class="vehicles-mobile-card"
          :class="{
            'vehicles-mobile-card--selected': index === activeRow,
          }"
          @click="activeRow = activeRow === index ? null : index"
        >
          <v-chip
            class="vehicles-mobile-card__date"
            size="x-small"
            variant="outlined"
            color="black"
          >
            {{ formatDate(item.date_created) }}
          </v-chip>

          <div class="vehicles-mobile-card__main">
            <v-btn
              icon
              variant="text"
              size="small"
              class="flex-shrink-0 mt-1"
              :loading="loadingSelect[index.toString()]"
              @click.stop="selectVehicle(item, index)"
            >
              <v-icon v-if="item.selected" color="grey-darken-2">
                mdi-checkbox-marked-outline
              </v-icon>
              <v-icon v-else color="grey-darken-2">
                mdi-checkbox-blank-outline
              </v-icon>
            </v-btn>

            <div class="vehicles-mobile-card__media">
              <button
                type="button"
                class="vehicles-mobile-card__image-btn"
                @click.stop="showImages(item.photos)"
              >
                <img
                  :src="
                    item.photos && item.photos.length
                      ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.photos[0].thumb}`
                      : `https://portal.lekotech.com/logo_small.png`
                  "
                  class="vehicles-mobile-card__img"
                />
              </button>
              <button
                type="button"
                class="vehicles-mobile-card__qr-btn"
                @click.stop="
                  openQrDialog({
                    id: item._id,
                    type: 'cars',
                    vehicle: item,
                    printed: item.printed,
                  })
                "
              >
                {{ item._id.slice(item._id.length - 6, item._id.length) }}
              </button>
            </div>

            <div class="vehicles-mobile-card__info">
              <div class="vehicles-mobile-card__info-top">
                <v-chip
                v-if="
                  ($store.getters.role === 'LekoAdmin' ||
                    $store.getters.role === 'SuperAdmin') &&
                  item.institution_id?.title
                "
                   class=" text-xs"
                 size="x-small"
              >
                {{ item.institution_id.title }}
              </v-chip>
                <div class="font-weight-bold text-primary text-body-2">
                  {{ item.brand.title }}
                </div>
                <div class="text-body-2">
                  {{ item.model.description }}
                </div>
                <div class="text-caption">
                  {{ item.version.description }}
                </div>
              </div>

              <div class="text-caption mt-1">
                {{ formatMonthYear(item.version.startingDate) }} -
                {{ formatMonthYear(item.version.endingDate) }}
              </div>
              <div class="text-caption mt-1">
                <span v-if="item.registrationYear">
                  {{ $t("vehicle_entry.datatable.registration_year") }}:
                  <strong>{{ item.registrationYear }}</strong>
                </span>
                <span v-if="item.km != null" class="ml-2">
                  {{ $t("vehicle_entry.datatable.number_of_km") }}:
                  <strong>{{ item.km }}</strong>
                </span>
              </div>
              <div v-if="item.pra" class="text-caption mt-1">
                {{ $t("vehicle_entry.datatable.practice_number") }}:
                <strong>{{ item.pra }}</strong>
              </div>
              <div class="text-caption mt-1">
                <span v-if="item.version.numberOfDoors">
                  {{ item.version.numberOfDoors }}
                  {{ $t("vehicle_entry.datatable.doors") }}
                </span>
                <span
                  v-if="
                    item.version &&
                    item.version.power &&
                    item.version.power.kw
                  "
                  class="ml-2"
                >
                  {{ item.version.power.kw }} kw
                </span>
              </div>
              <div
                v-if="item.version.engineCode || item.version.supplyCode"
                class="text-caption mt-1"
              >
                <span v-if="item.version.engineCode">
                  {{ $t("vehicle_entry.datatable.engine") }}:
                  {{ item.version.engineCode }}
                </span>
                <span v-if="item.version.supplyCode" class="ml-2">
                  {{ getSupplyLocale(item.version.supplyCode) }}
                </span>
              </div>
              <div
                v-if="item.transmissionType || item.transmissionLayout"
                class="text-caption mt-1"
              >
                {{ item.transmissionType }}
                <span v-if="item.transmissionLayout">
                  · {{ item.transmissionLayout }}
                </span>
              </div>
              <div
                v-if="item.location && item.location.name"
                class="text-caption text-grey-darken-1 mt-1"
              >
                {{ item.location.name }}
              </div>
              <v-chip
                v-if="item.video"
                size="x-small"
                color="grey-darken-1"
                variant="tonal"
                class="mt-1 pointer"
                @click.stop="showVideo(item.video)"
              >
                <v-icon start size="12">mdi-video-outline</v-icon>
                {{ $t("vehicle_entry.datatable.video") }}
              </v-chip>
            </div>
          </div>

          <div class="vehicles-mobile-card__actions">
            <compliance-summary-pill
              v-if="praEnabled || workshopEnabled"
              :car="item"
              :summary="mobileSummary(item)"
              :failed="acceptanceFailed"
            />
            <v-btn
              v-if="praEnabled && acceptanceByCar[item._id]"
              icon
              size="small"
              color="primary"
              variant="tonal"
              :title="$t('acceptance.list.open')"
              @click.stop="openAcceptance(item)"
            >
              <v-icon size="20">mdi-clipboard-check-outline</v-icon>
            </v-btn>
            <span
              v-if="$store.getters.role === 'SuperAdmin'"
              class="infocar-parts d-inline-flex align-center mx-2"
              :title="
                item.hasInfocarParts
                  ? $t('vehicle_entry.datatable.has_infocar_parts')
                  : $t('vehicle_entry.datatable.no_infocar_parts')
              "
            >
              <v-icon
                size="20"
                :color="item.hasInfocarParts ? 'primary' : 'grey'"
              >
                mdi-information
              </v-icon>
              <v-icon
                v-if="item.hasInfocarParts"
                class="infocar-parts__check"
                color="green"
                size="12"
              >
                mdi-check-circle
              </v-icon>
            </span>
            <v-btn icon variant="text" size="small" @click.stop="share(item)">
              <v-icon size="20" color="grey-darken-2">
                mdi-share-variant-outline
              </v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              @click.stop="viewDetails(item)"
            >
              <v-icon size="20" color="grey-darken-2">
                mdi-format-list-bulleted
              </v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              @click.stop="openQrGenerationDialog(item)"
            >
              <v-icon size="20" color="grey-darken-2">mdi-qrcode</v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              @click.stop="openInfoCarCad(item)"
            >
              <v-icon size="20" color="grey-darken-2">mdi-car</v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              :loading="loadingReturn[index.toString()]"
              @click.stop="openConfirmModal(item, index)"
            >
              <v-icon size="20" color="grey-darken-2">mdi-history</v-icon>
            </v-btn>
          </div>
        </div>

        <v-alert
          v-if="!loading && cars.length < 1"
          type="info"
          variant="tonal"
          class="mt-2"
        >
          No data available.
        </v-alert>
      </div>
      <!-- ===================================================================== -->

      <div class="vehicles-table-host pb-2">
        <StickyHScroll>
          <v-data-table-server
          v-model:options="pagination"
          :headers="tableHeaders"
          :items="cars"
          :page="pagination.page"
          :items-length="total"
          :loading="loading"
          class="elevation-1 nowrap-headers pb-8"
          :items-per-page="pagination.itemsPerPage"
          :items-per-page-options="[
            { value: 5, title: '5' },
            { value: 10, title: '10' },
            { value: 25, title: '25' },
            { value: 50, title: '50' },
          ]"
          items-per-page-text="Rows per page"
          @update:options="updatePagination"
        >
          <template #progress>
            <v-progress-linear color="red" indeterminate />
          </template>

          <template #item="{ item, index }">
            <tr>
              <td>
                <v-btn
                  icon
                  variant="text"
                  :loading="loadingSelect[index.toString()]"
                  @click="selectVehicle(item, index)"
                >
                  <v-icon v-if="item.selected" color="grey-darken-2">
                    mdi-checkbox-marked-outline
                  </v-icon>
                  <v-icon v-else color="grey-darken-2">
                    mdi-checkbox-blank-outline
                  </v-icon>
                </v-btn>
              </td>
              <td class="ma-1 pt-1 pb-1">
                <v-row
                  class="pointer ma-1 text-center justify-center d-block"
                  @click="
                    openQrDialog({
                      id: item._id,
                      type: 'cars',
                      vehicle: item,
                      printed: item.printed,
                    })
                  "
                >
                  <qr-code :size="60" :text="item._id" />
                  <h4>
                    {{ item._id.slice(item._id.length - 6, item._id.length) }}
                  </h4>
                </v-row>
              </td>
              <td>
                <v-row class="d-flex flex-nowrap">
                  <v-btn
                    v-if="praEnabled && acceptanceByCar[item._id]"
                    icon
                    color="primary"
                    variant="tonal"
                    :title="$t('acceptance.list.open')"
                    @click="openAcceptance(item)"
                  >
                    <v-icon>mdi-clipboard-check-outline</v-icon>
                  </v-btn>

                  <!--SHARE-->
                  <v-btn icon variant="text" @click="share(item)">
                    <v-icon color="grey-darken-2">
                      mdi-share-variant-outline
                    </v-icon>
                  </v-btn>

                  <v-btn icon variant="text" @click="viewDetails(item)">
                    <v-icon color="grey-darken-2">
                      mdi-format-list-bulleted
                    </v-icon>
                  </v-btn>

                  <v-btn
                    icon
                    variant="text"
                    @click="openQrGenerationDialog(item)"
                  >
                    <v-icon color="grey-darken-2"> mdi-qrcode </v-icon>
                  </v-btn>

                  <span
                    v-if="$store.getters.role === 'SuperAdmin'"
                    class="infocar-parts d-inline-flex align-center mr-2"
                    :title="
                      item.hasInfocarParts
                        ? $t('vehicle_entry.datatable.has_infocar_parts')
                        : $t('vehicle_entry.datatable.no_infocar_parts')
                    "
                  >
                    <v-icon :color="item.hasInfocarParts ? 'primary' : 'grey'">
                      mdi-information
                    </v-icon>
                    <v-icon
                      v-if="item.hasInfocarParts"
                      class="infocar-parts__check"
                      color="green"
                      size="14"
                    >
                      mdi-check-circle
                    </v-icon>
                  </span>

                  <v-btn icon variant="text" @click="openInfoCarCad(item)">
                    <v-icon color="grey-darken-2"> mdi-car </v-icon>
                  </v-btn>

                  <v-btn
                    icon
                    variant="text"
                    :loading="loadingReturn[index.toString()]"
                    @click="openConfirmModal(item, index)"
                  >
                    <v-icon color="grey-darken-2"> mdi-history </v-icon>
                  </v-btn>
                </v-row>
              </td>
              <td>
                <img
                  :src="
                    item.photos && item.photos.length
                      ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.photos[0].thumb}`
                      : `https://portal.lekotech.com/logo_small.png`
                  "
                  class="ma-1 pointer"
                  height="75"
                  width="100"
                  @click="showImages(item.photos)"
                />
              </td>
              <td>
                <video
                  v-if="item.video"
                  :src="`https://leko-tech.fra1.cdn.digitaloceanspaces.com/${item.video}`"
                  class="ma-1 pointer"
                  height="75"
                  width="100"
                  @click="showVideo(item.video)"
                />
              </td>
              <td
                v-if="
                  $store.getters.role === 'LekoAdmin' ||
                  $store.getters.role === 'SuperAdmin'
                "
              >
                {{ item.institution_id ? item.institution_id.title : "" }}
              </td>
              <td>{{ item.brand.title }}</td>
              <td>{{ item.model.description }}</td>
              <td>{{ item.version.description }}</td>
              <td>
                <div class="text-truncate">
                  {{ formatMonthYear(item.version.startingDate) }} -
                  {{ formatMonthYear(item.version.endingDate) }}
                </div>
              </td>
              <td>{{ item.registrationYear }}</td>
              <td>{{ item.km }}</td>
              <td>{{ item.pra }}</td>
              <td>
                {{ item.version.numberOfDoors }}
                {{ $t("vehicle_entry.datatable.doors") }}
              </td>
              <td>{{ item.version.power.kw }} kw</td>
              <td>{{ item.version.engineCode }}</td>
              <td>{{ getSupplyLocale(item.version.supplyCode) }}</td>
              <td>{{ item.transmissionType }}</td>
              <td>{{ item.transmissionLayout }}</td>
              <td>
                {{
                  item.location && item.location.name ? item.location.name : ""
                }}
              </td>
              <td class="text-start">
                <v-btn variant="outlined" small color="black">
                  {{ formatDate(item.date_created) }}
                </v-btn>
              </td>
              <td v-if="praEnabled">
                <span
                  v-if="acceptanceFailed || !acceptanceLoaded"
                  :title="
                    acceptanceFailed ? $t('acceptance.list.load_failed') : ''
                  "
                >
                  —
                </span>
                <span v-else-if="!praChips(item).length">—</span>
                <span v-else class="d-inline-flex flex-nowrap">
                  <v-chip
                    v-for="chip in praChips(item)"
                    :key="chip.key"
                    size="small"
                    variant="tonal"
                    class="mr-1"
                    :color="chip.color"
                  >
                    {{ $t(chip.key) }}
                  </v-chip>
                </span>
              </td>
              <td v-if="workshopEnabled">
                <span
                  v-if="acceptanceFailed || !acceptanceLoaded"
                  :title="
                    acceptanceFailed ? $t('acceptance.list.load_failed') : ''
                  "
                >
                  —
                </span>
                <template v-else>
                  <v-chip
                    v-if="workshopCell(item).chip"
                    size="small"
                    variant="tonal"
                    class="text-no-wrap"
                    :color="workshopCell(item).chip.color"
                    :title="workshopTitle(workshopCell(item))"
                  >
                    {{ workshopText(workshopCell(item)) }}
                  </v-chip>
                  <span v-else>—</span>
                  <div
                    v-if="workshopCell(item).dueDate"
                    class="text-caption text-no-wrap"
                  >
                    {{
                      $t("acceptance.depollution.list.due_on", {
                        date: workshopCell(item).dueDate,
                      })
                    }}
                  </div>
                </template>
              </td>
            </tr>
            <tr v-if="cars && cars.length < 1">
              <td :colspan="headers.length" class="text-center pa-4">
                <v-alert type="info" border="left" color="red lighten-2" text>
                  No data available.
                </v-alert>
              </td>
            </tr>
          </template>
        </v-data-table-server>
        </StickyHScroll>
      </div>

      <v-dialog v-model="confirmModal" hide-overlay persistent width="500">
        <v-card>
          <v-card-text class="pt-4 pb-0 mb-0">
            <v-alert :value="true" type="error" border="bottom" variant="tonal">
              {{ $t("vehicle_entry.datatable.confirmAlertInv") }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              variant="outlined"
              color="red-darken-1"
              @click="confirmModal = false"
            >
              {{ $t("vehicle_entry.info.no") }}
            </v-btn>
            <v-btn
              variant="outlined"
              color="blue-darken-1"
              @click="returnInventory"
            >
              {{ $t("vehicle_entry.info.yes") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script>
import moment from "moment";
import VueQrCode from "vue-qr-generator";

import getRequest from "../../api/getRequest";
import putRequest from "../../api/putRequest";
import { getDossiersByCars } from "@/api/compliance";
import { listChips } from "@/components/Acceptance/praView.mjs";
import { listCell } from "@/components/Workshop/workshopView.mjs";
import { complianceSummary } from "@/components/VehicleEntry/complianceSummary.mjs";
import ComplianceSummaryPill from "@/components/VehicleEntry/complianceSummaryPill.vue";
// import deleteRequest from '../../api/deleteRequest';
let lastRequestId = 0;

export default {
  components: {
    "qr-code": VueQrCode,
    ComplianceSummaryPill,
  },
  filters: {
    date(date) {
      moment.locale(localStorage.getItem("language"));
      const valid = moment(date).isValid();
      if (valid) return moment(date).format("MM/YYYY");
      return "";
    },
    fullDate(date) {
      moment.locale(localStorage.getItem("language"));
      return moment(date).format("LLL");
    },
  },
  data: () => ({
    headers: [
      { title: "Selected", sortable: false, key: "" },
      { title: "ID", sortable: true, key: "_id" },
      { title: "Actions", sortable: false, key: "" },
      { title: "Photo", sortable: false, key: "" },
      { title: "Video", sortable: false, key: "" },
      { title: "Brand", sortable: true, key: "brand.brandCode" },
      { title: "Model", sortable: true, key: "model.modelCode" },
      { title: "Version", sortable: true, key: "version.versionCode" },
      { title: "Year", sortable: false, key: "" },
      { title: "Registration Year", sortable: false, key: "" },
      { title: "KM", sortable: false, key: "" },
      { title: "PRA", sortable: true, key: "pra" },
      { title: "Number of doors", sortable: false, key: "" },
      { title: "Power", sortable: false, key: "" },
      { title: "Engine", sortable: false, key: "" },
      { title: "Supply", sortable: false, key: "" },
      { title: "Transmission Type", sortable: false, key: "" },
      { title: "Transmission Layout", sortable: false, key: "" },
      { title: "Location", sortable: true, key: "location.name" },
      { title: "Date Created", sortable: true, key: "date" },
    ],
    headersAdmin: [
      { title: "Selected", sortable: false, key: "" },
      { title: "ID", sortable: true, key: "_id" },
      { title: "Actions", sortable: false, key: "" },
      { title: "Photo", sortable: false, key: "" },
      { title: "Video", sortable: false, key: "" },
      { title: "Institution", sortabe: true, key: "institution_id" },
      { title: "Brand", sortable: true, key: "brand.brandCode" },
      { title: "Model", sortable: true, key: "model.modelCode" },
      { title: "Version", sortable: true, key: "version.versionCode" },
      { title: "Year", sortable: false, key: "" },
      { title: "Registration Year", sortable: false, key: "" },
      { title: "KM", sortable: false, key: "" },
      { title: "PRA", sortable: true, key: "pra" },
      { title: "Number of doors", sortable: false, key: "" },
      { title: "Power", sortable: false, key: "" },
      { title: "Engine", sortable: false, key: "" },
      { title: "Supply", sortable: false, key: "" },
      { title: "Transmission Type", sortable: false, key: "" },
      { title: "Transmission Layout", sortable: false, key: "" },
      { title: "Location", sortable: true, key: "location.name" },
      { title: "Date Created", sortable: true, key: "date" },
    ],
    pagination: {
      itemsPerPage: 5,
      page: 1,
      groupBy: ["date_created"],
      sortBy: [{ key: "date_created", order: "desc" }],
      sortDesc: [true],
    },
    filters: {
      type: null,
      search: null,
      location: null,
      part: null,
      brand: null,
      model: null,
      version: null,
      engine: null,
      km: [0, 1000000],
      startDate: null,
      endDate: null,
    },
    cars: [],
    total: 0,
    loading: false,
    loadingSelect: {},
    loadingReturn: {},
    confirmModal: false,
    vehicle: {},
    index: "",
    activeRow: null,
    // PRA case and workshop checklist per removed car (only used when praEnabled or workshopEnabled).
    acceptanceByCar: {}, // carId -> { carId, dossierId, status, deadlines, pra?, workshop? }
    acceptanceLoaded: false,
    acceptanceFailed: false,
  }),
  computed: {
    server() {
      return this.$serverUri;
    },
    praEnabled() {
      return this.$store.getters.praEnabled;
    },
    workshopEnabled() {
      return this.$store.getters.workshopEnabled;
    },
    tableHeaders() {
      const base =
        this.$store.getters.role === "LekoAdmin" ||
        this.$store.getters.role === "SuperAdmin"
          ? this.headersAdmin
          : this.headers;
      if (!this.praEnabled && !this.workshopEnabled) return base;
      return [
        ...base,
        ...(this.praEnabled
          ? [
              {
                title: this.$t("acceptance.pra.title"),
                sortable: false,
                key: "acceptancePra",
              },
            ]
          : []),
        ...(this.workshopEnabled
          ? [
              {
                title: this.$t("acceptance.depollution.column"),
                sortable: false,
                key: "acceptanceWorkshop",
              },
            ]
          : []),
      ];
    },
  },
  watch: {
    pagination: {
      handler() {
        this.getCars();
      },
    },
  },
  created() {
    this.getLocale();
    this.getCars();
  },
  methods: {
    getLocale() {
      this.headers[0].title = this.$t("vehicle_entry.datatable.selected");
      this.headers[1].title = this.$t("vehicle_entry.datatable.id");
      this.headers[2].title = this.$t("vehicle_entry.datatable.actions");
      this.headers[3].title = this.$t("vehicle_entry.datatable.images");
      this.headers[4].title = this.$t("vehicle_entry.datatable.video");
      this.headers[5].title = this.$t("vehicle_entry.datatable.brand");
      this.headers[6].title = this.$t("vehicle_entry.datatable.model");
      this.headers[7].title = this.$t("vehicle_entry.datatable.version");
      this.headers[8].title = this.$t("vehicle_entry.datatable.year");
      this.headers[9].title = this.$t(
        "vehicle_entry.datatable.registration_year",
      );
      this.headers[10].title = this.$t("vehicle_entry.datatable.number_of_km");
      this.headers[11].title = this.$t(
        "vehicle_entry.datatable.practice_number",
      );
      this.headers[12].title = this.$t(
        "vehicle_entry.datatable.number_of_doors",
      );
      this.headers[13].title = this.$t("vehicle_entry.datatable.power");
      this.headers[14].title = this.$t("vehicle_entry.datatable.engine");
      this.headers[15].title = this.$t("vehicle_entry.datatable.supply");
      this.headers[16].title = this.$t(
        "vehicle_entry.datatable.transmission_type",
      );
      this.headers[17].title = this.$t(
        "vehicle_entry.datatable.transmission_layout",
      );
      this.headers[18].title = this.$t("vehicle_entry.datatable.location");
      this.headers[19].title = this.$t("vehicle_entry.datatable.date_created");
      this.headersAdmin[0].title = this.$t("vehicle_entry.datatable.selected");
      this.headersAdmin[1].title = this.$t("vehicle_entry.datatable.id");
      this.headersAdmin[2].title = this.$t("vehicle_entry.datatable.actions");
      this.headersAdmin[3].title = this.$t("vehicle_entry.datatable.images");
      this.headersAdmin[4].title = this.$t("vehicle_entry.datatable.video");
      this.headersAdmin[5].title = this.$t(
        "vehicle_entry.datatable.institution",
      );
      this.headersAdmin[6].title = this.$t("vehicle_entry.datatable.brand");
      this.headersAdmin[7].title = this.$t("vehicle_entry.datatable.model");
      this.headersAdmin[8].title = this.$t("vehicle_entry.datatable.version");
      this.headersAdmin[9].title = this.$t("vehicle_entry.datatable.year");
      this.headersAdmin[10].title = this.$t(
        "vehicle_entry.datatable.registration_year",
      );
      this.headersAdmin[11].title = this.$t(
        "vehicle_entry.datatable.number_of_km",
      );
      this.headersAdmin[12].title = this.$t(
        "vehicle_entry.datatable.practice_number",
      );
      this.headersAdmin[13].title = this.$t(
        "vehicle_entry.datatable.number_of_doors",
      );
      this.headersAdmin[14].title = this.$t("vehicle_entry.datatable.power");
      this.headersAdmin[15].title = this.$t("vehicle_entry.datatable.engine");
      this.headersAdmin[16].title = this.$t("vehicle_entry.datatable.supply");
      this.headersAdmin[17].title = this.$t(
        "vehicle_entry.datatable.transmission_type",
      );
      this.headersAdmin[18].title = this.$t(
        "vehicle_entry.datatable.transmission_layout",
      );
      this.headersAdmin[19].title = this.$t("vehicle_entry.datatable.location");
      this.headersAdmin[20].title = this.$t(
        "vehicle_entry.datatable.date_created",
      );
    },
    getCars() {
      const requestId = ++lastRequestId;
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      // eslint-disable-next-line no-nested-ternary
      getRequest(
        "/car",
        `status=2&institution_id=${
          this.$store.getters.role !== "LekoAdmin" &&
          this.$store.getters.role !== "SuperAdmin"
            ? this.$store.getters.institutionId
            : this.filters.institution
              ? this.filters.institution
              : ""
        }&role=${this.$store.getters.role}&page=${this.pagination.page}&limit=${
          this.pagination.itemsPerPage
        }&sort_by=${sort.key}&descending=${
          sort.order === "desc"
        }&type=${this.filters.type ? this.filters.type : ""}&search=${this.filters.search ? this.filters.search : ""}&part=${
          this.filters.part ? this.filters.part : ""
        }&km=${this.filters.km}&brand=${this.filters.brand ? this.filters.brand : ""}&model=${
          this.filters.model ? this.filters.model : ""
        }&engine=${this.filters.engine ? this.filters.engine : ""}&version=${
          this.filters.version ? this.filters.version : ""
        }&startDate=${this.filters.startDate ? this.filters.startDate : ""}&endDate=${this.filters.endDate ? this.filters.endDate : ""}&location=${this.filters.location ? this.filters.location : ""}`,
      )
        .then((response) => {
          if (requestId !== lastRequestId) return;
          this.cars = response.cars;
          this.total = response.count;
          this.loading = false;
          this.$emit("cars", this.cars);
          this.loadAcceptance(requestId);
        })
        .catch(() => {
          this.loading = false;
        })
        .finally(() => {});
    },
    // PRA case and workshop checklist of the removed cars on this page: one by-cars call, after the
    // list loaded. A failure leaves the list working and shows a dash with a tooltip. No request
    // with both PRA and the workshop off.
    loadAcceptance(requestId) {
      if (!this.praEnabled && !this.workshopEnabled) return;
      this.acceptanceLoaded = false;
      this.acceptanceFailed = false;
      this.acceptanceByCar = {};
      const carIds = (this.cars || []).map((car) => car._id).slice(0, 50);
      if (!carIds.length) {
        this.acceptanceLoaded = true;
        return;
      }
      getDossiersByCars(carIds)
        .then((response) => {
          if (requestId !== lastRequestId) return;
          const byCar = {};
          (response.dossiers || []).forEach((row) => {
            byCar[row.carId] = row;
          });
          this.acceptanceByCar = byCar;
          this.acceptanceLoaded = true;
        })
        .catch(() => {
          if (requestId !== lastRequestId) return;
          this.acceptanceFailed = true;
        });
    },
    // "Pratica PRA" chips of the car's case; [] (shown as "—") while loading or without a case.
    praChips(car) {
      if (!this.acceptanceLoaded || this.acceptanceFailed) return [];
      const row = this.acceptanceByCar[car._id];
      return listChips(row && row.pra);
    },
    // "Bonifica" cell of the car's checklist: { chip, dueDate }; no chip (shown as "—") while
    // loading, without a checklist or for a draft.
    workshopCell(car) {
      if (!this.acceptanceLoaded || this.acceptanceFailed) {
        return { chip: null, dueDate: "" };
      }
      return listCell(this.acceptanceByCar[car._id]);
    },
    // Mobile card "Compliance" pill and modal (PRA and depollution only); null (shown as "—")
    // while loading or after a failure.
    mobileSummary(car) {
      if (!this.acceptanceLoaded || this.acceptanceFailed) return null;
      return complianceSummary(this.acceptanceByCar[car._id], {
        acceptance: false,
        praEnabled: this.praEnabled,
        workshopEnabled: this.workshopEnabled,
      });
    },
    workshopText(cell) {
      return cell.chip ? this.$t(cell.chip.key, cell.chip.params) : "";
    },
    workshopTitle(cell) {
      const title = cell.chip && cell.chip.title;
      return title ? this.$t(title.key, title.params) : undefined;
    },
    // Opens the removed car's acceptance; its back link returns to Storico (DF10). No start here.
    openAcceptance(car) {
      const row = this.acceptanceByCar[car._id];
      if (row) this.$router.push(`/acceptance/${row.dossierId}`);
    },
    setFilters(filters) {
      this.filters = filters;
      this.getCars();
    },
    viewDetails(car) {
      this.$emit("view-details", car);
    },
    share(data) {
      this.$emit("share", data);
    },
    selectVehicle(vehicle, index) {
      this.loadingSelect[index.toString()] = true;
      putRequest("/car/select", `id=${vehicle._id}`, {})
        .then((result) => {
          this.getCars();
        })
        .catch(() => {})
        .finally(() => {
          this.loadingSelect[index.toString()] = false;
        });
    },
    openConfirmModal(vehicle, index) {
      this.confirmModal = true;
      this.vehicle = vehicle;
      this.index = index;
    },
    returnInventory() {
      this.loadingReturn[this.index.toString()] = true;
      putRequest("/car/inventory", `id=${this.vehicle._id}`)
        .then((response) => {
          this.getCars();
          this.confirmModal = false;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingReturn[this.index.toString()] = false;
          this.$emit("returned");
        });
    },
    showImages(images) {
      this.$emit("images", images);
    },
    showVideo(video) {
      this.$emit("video", video);
    },
    openQrDialog(data) {
      this.$emit("qr", data);
    },
    openQrGenerationDialog(data) {
      this.$emit("generate-qr", data);
    },
    openInfoCarCad(car) {
      this.$emit("infocar-cad", {
        brandCode: car.brand.brandCode,
        modelCode: car.model.modelCode,
        versionCode: car.version.versionCode,
      });
    },
    getSupplyLocale(supply) {
      switch (supply) {
        case "B":
          return this.$t("parts.advanced_search.supply.petrol");
        case "D":
          return this.$t("parts.advanced_search.supply.diesel");
        case "G":
          return this.$t("parts.advanced_search.supply.gas");
        default:
          return supply;
      }
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    formatMonthYear(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // months are 0-indexed
      const year = date.getUTCFullYear();
      return `${month}/${year}`;
    },
    updatePagination(options) {
      this.pagination = {
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        groupBy:
          options.groupBy && options.groupBy.length
            ? options.groupBy
            : ["date_created"],
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : { key: "date_created", order: "desc" },
        sortDesc:
          options.sortDesc && options.sortDesc.length
            ? options.sortDesc
            : [true],
      };
      this.activeRow = null;
    },
  },
};
</script>

<style scoped>
/* ── Mobile / Desktop visibility toggle ── */
@media (max-width: 959.98px) {
  .vehicles-table-host :deep(.v-table__wrapper),
  .vehicles-table-host :deep(.v-data-table__wrapper),
  .vehicles-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .vehicles-table-host :deep(.global-sticky-hscroll) {
    display: none !important;
  }
  .vehicles-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .vehicles-mobile-list {
    display: none !important;
  }
}

/* ── Mobile card styles ── */
.vehicles-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.vehicles-mobile-card__date {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 0.7rem !important;
  max-width: 55%;
}
.vehicles-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.vehicles-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 8px;
  gap: 8px;
}
.vehicles-mobile-card__media {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.vehicles-mobile-card__image-btn {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.vehicles-mobile-card__img {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  background: #212121;
  display: block;
}
.vehicles-mobile-card__qr-btn {
  min-width: 72px;
  margin-top: 2px;
  padding: 4px 8px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  background: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.2;
  color: #424242;
  cursor: pointer;
  text-align: center;
}
.vehicles-mobile-card__info {
  flex: 1;
  min-width: 0;
}
.vehicles-mobile-card__info-top {
  padding-right: 88px;
}
.vehicles-mobile-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 2px 4px;
}

/* ── Infocar parts availability indicator ── */
.infocar-parts {
  position: relative;
}
.infocar-parts__check {
  position: absolute;
   right: -8px;
  top: 6px;
  background-color: #fff;
  border-radius: 50%;
}
</style>
